'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import BrandHeader from '@/components/common/BrandHeader';
import ProgressIndicator from '@/components/common/ProgressIndicator';
import ProfileCard from './ProfileCard';
import ScoreBreakdown from './ScoreBreakdown';
import RecommendationsPanel from './RecommendationsPanel';
import ActionPanel from './ActionPanel';
import ContactRequestForm from './ContactRequestForm';
import AppImage from '@/components/ui/AppImage';

const GESTPUB_LOGO_URL =
  'https://bcaab5b374.clvaw-cdnwnd.com/85a140df190163a9b35601938dceb57e/2001-dcd1edcd20/logo.png?ph=bcaab5b374';

export interface DiagnosticResults {
  totalScore: number;
  profile: 'stable' | 'punctual' | 'support' | 'pressure';
  areaScores: {
    administrativeOrganization: number;
    financialManagement: number;
    humanResources: number;
    publicProcurement: number;
  };
  contactInfo: {
    name: string;
    parish: string;
    municipality: string;
    position: string;
    email: string;
    phone: string;
    employeeCount: string;
    voterCount: string;
  };
  supportPreference: string;
  pressurePoints: string[];
  operationalChallenges: string[];
}

function deriveProfile(score: number): DiagnosticResults['profile'] {
  if (score <= 4) return 'stable';
  if (score <= 8) return 'punctual';
  if (score <= 12) return 'support';
  return 'pressure';
}

const PROFILE_LABELS: Record<DiagnosticResults['profile'], string> = {
  stable: 'Estrutura Globalmente Estável',
  punctual: 'Necessidades de Reforço Pontual',
  support: 'Necessidade Clara de Apoio Técnico',
  pressure: 'Pressão Operacional Elevada',
};

const CRITICAL_AREAS_BY_PROFILE: Record<DiagnosticResults['profile'], string[]> = {
  stable: ['Organização Administrativa', 'Gestão Financeira', 'Recursos Humanos'],
  punctual: ['Contratação Pública', 'Gestão Financeira', 'Organização Administrativa'],
  support: ['Gestão Financeira', 'Contratação Pública', 'Recursos Humanos'],
  pressure: ['Intervenção Global', 'Gestão Financeira e Contratação', 'Capacitação da Equipa'],
};

const RECOMMENDATIONS_BY_PROFILE: Record<DiagnosticResults['profile'], string[]> = {
  stable: [
    'Implementar revisão periódica de processos',
    'Otimizar relatórios de execução orçamental',
    'Desenvolver plano de formação contínua',
  ],
  punctual: [
    'Apoio técnico em procedimentos de contratação',
    'Revisão do sistema de controlo interno',
    'Digitalização e arquivo documental',
  ],
  support: [
    'Implementação urgente de suporte financeiro especializado',
    'Regularização de procedimentos de contratação',
    'Reestruturação da gestão de recursos humanos',
  ],
  pressure: [
    'Diagnóstico aprofundado e plano de recuperação operacional',
    'Suporte técnico contínuo em áreas críticas',
    'Programa intensivo de capacitação técnica',
  ],
};

const emptyResults: DiagnosticResults = {
  totalScore: 0,
  profile: 'stable',
  areaScores: {
    administrativeOrganization: 0,
    financialManagement: 0,
    humanResources: 0,
    publicProcurement: 0,
  },
  contactInfo: {
    name: '',
    parish: '',
    municipality: '',
    position: '',
    email: '',
    phone: '',
    employeeCount: '',
    voterCount: '',
  },
  supportPreference: '',
  pressurePoints: [],
  operationalChallenges: [],
};

export default function ResultsInteractive() {
  const [results, setResults] = useState<DiagnosticResults>(emptyResults);
  const [showContactForm, setShowContactForm] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [emailReportSent, setEmailReportSent] = useState(false);
  const [emailReportError, setEmailReportError] = useState<string | null>(null);
  const [emailReportLoading, setEmailReportLoading] = useState(false);
  const [meetingRequestSent, setMeetingRequestSent] = useState(false);
  const [meetingRequestError, setMeetingRequestError] = useState<string | null>(null);
  const [meetingRequestLoading, setMeetingRequestLoading] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('diagnosticResults');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const score: number = parsed.totalScore ?? 0;
          const mapped: DiagnosticResults = {
            totalScore: score,
            profile: deriveProfile(score),
            areaScores: {
              administrativeOrganization: parsed.areaScores?.adminOrg ?? 0,
              financialManagement: parsed.areaScores?.financialMgmt ?? 0,
              humanResources: parsed.areaScores?.humanResources ?? 0,
              publicProcurement: parsed.areaScores?.publicProcurement ?? 0,
            },
            contactInfo: {
              name: parsed.contact?.name ?? '',
              parish: parsed.contact?.parishCouncil ?? '',
              municipality: parsed.contact?.municipality ?? '',
              position: parsed.contact?.position ?? '',
              email: parsed.contact?.email ?? '',
              phone: parsed.contact?.phone ?? '',
              employeeCount: parsed.contact?.employeeCount ?? '',
              voterCount: parsed.contact?.voterCount ?? '',
            },
            supportPreference: parsed.support?.preference ?? '',
            pressurePoints: parsed.pressure?.mainChallenges ?? [],
            operationalChallenges: parsed.pressure?.operationalFrequencies ?? [],
          };
          setResults(mapped);

          // Duplicate-prevention layer 1: sessionStorage flag (tab-scoped)
          // Duplicate-prevention layer 2: stable submissionId derived from email + junta_freguesia
          // The server uses submissionId to reject duplicate inserts for the same identifier.
          const alreadySaved = sessionStorage.getItem('diagnosticSaved') === 'true';

          // Build a stable submission identifier from email + junta_freguesia
          const rawEmail = (parsed.contact?.email ?? '').trim().toLowerCase();
          const rawJunta = (parsed.contact?.parishCouncil ?? '').trim().toLowerCase();
          const submissionId = rawEmail && rawJunta
            ? `${rawEmail}::${rawJunta}`
            : null;

          // Save to Supabase — only if we have real contact data and haven't saved yet
          if (!alreadySaved && parsed.contact?.name && parsed.contact?.email) {
            const profile = deriveProfile(score);
            saveDiagnosticToSupabase({
              nome: parsed.contact?.name ?? '',
              cargo: parsed.contact?.position ?? '',
              email: parsed.contact?.email ?? '',
              telefone: parsed.contact?.phone ?? '',
              junta_freguesia: parsed.contact?.parishCouncil ?? '',
              municipio: parsed.contact?.municipality ?? '',
              numero_colaboradores: parsed.contact?.employeeCount ?? '',
              numero_eleitores: parsed.contact?.voterCount ?? '',
              score_total: score,
              perfil_resultado: PROFILE_LABELS[profile],
              areas_criticas: CRITICAL_AREAS_BY_PROFILE[profile],
              recomendacoes: RECOMMENDATIONS_BY_PROFILE[profile],
              submissionId: submissionId ?? undefined,
            });
          }
        } catch {
          // keep emptyResults if parsing fails
        }
      }
    }
  }, []);

  const saveDiagnosticToSupabase = async (payload: {
    nome: string;
    cargo: string;
    email: string;
    telefone: string;
    junta_freguesia: string;
    municipio: string;
    numero_colaboradores: string;
    numero_eleitores: string;
    score_total: number;
    perfil_resultado: string;
    areas_criticas: string[];
    recomendacoes: string[];
    submissionId?: string;
  }) => {
    try {
      const response = await fetch('/api/save-diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // If server says duplicate, treat as already saved (not an error)
        if (data?.duplicate === true) {
          sessionStorage.setItem('diagnosticSaved', 'true');
          return;
        }
        setSaveError(
          data?.error || 'Não foi possível guardar o diagnóstico. Por favor contacte o suporte.'
        );
      } else {
        // Mark this submission as saved so page refreshes don't create duplicates
        sessionStorage.setItem('diagnosticSaved', 'true');

        // Fire internal GestPub notification with full diagnostic summary (fire-and-forget)
        fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'notification',
            results: {},
            diagnostic: {
              ...payload,
              created_at: new Date().toISOString(),
            },
          }),
        }).catch((err) => {
          console.warn('[ResultsInteractive] GestPub notification failed (non-blocking):', err);
        });
      }
    } catch {
      setSaveError(
        'Erro de ligação ao guardar o diagnóstico. Por favor verifique a sua ligação à internet.'
      );
    }
  };

  const handleEmailReport = async () => {
    setEmailReportLoading(true);
    setEmailReportError(null);

    const userEmail = results.contactInfo.email?.trim();
    if (!userEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userEmail)) {
      setEmailReportError('Não foi possível enviar o relatório: endereço de email inválido ou em falta.');
      setEmailReportLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'report',
          results: {
            contactInfo: results.contactInfo,
            totalScore: results.totalScore,
            profile: results.profile,
            areaScores: results.areaScores,
            pressurePoints: results.pressurePoints,
          },
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.success === false) {
        const errMsg = data?.error || 'Erro desconhecido ao enviar o relatório.';
        setEmailReportError(`Não foi possível enviar o relatório para ${userEmail}. ${errMsg}`);
      } else {
        setEmailReportSent(true);
      }
    } catch {
      setEmailReportError('Erro de ligação ao enviar o relatório. Por favor verifique a sua ligação à internet.');
    } finally {
      setEmailReportLoading(false);
    }
  };

  const handleContactRequest = () => {
    setShowContactForm(true);
  };

  const handleMeetingRequest = async (periodoPreferencial: string, observacoes: string) => {
    setMeetingRequestLoading(true);
    setMeetingRequestError(null);

    try {
      // 1. Store in Supabase (fire-and-forget — DB failure must not block the user flow)
      const supabasePayload = {
        nome: results.contactInfo.name,
        cargo: results.contactInfo.position,
        email: results.contactInfo.email,
        telefone: results.contactInfo.phone,
        junta_freguesia: results.contactInfo.parish,
        municipio: results.contactInfo.municipality,
        score_total: results.totalScore,
        perfil_resultado: PROFILE_LABELS[results.profile],
        periodo_preferencial: periodoPreferencial || null,
        observacoes: observacoes || null,
      };

      console.log('[handleMeetingRequest] Sending to /api/save-meeting-request:', supabasePayload);

      fetch('/api/save-meeting-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(supabasePayload),
      })
        .then((r) => r.json().catch(() => ({})))
        .then((d) => console.log('[handleMeetingRequest] Supabase save result:', d))
        .catch((e) => console.warn('[handleMeetingRequest] Supabase save error (non-blocking):', e));

      // 2. Send email notification to GestPub
      const emailPayload = {
        type: 'meeting_request',
        meetingRequest: {
          nome: results.contactInfo.name,
          cargo: results.contactInfo.position,
          email: results.contactInfo.email,
          telefone: results.contactInfo.phone,
          junta_freguesia: results.contactInfo.parish,
          municipio: results.contactInfo.municipality,
          score_total: results.totalScore,
          perfil_resultado: PROFILE_LABELS[results.profile],
          periodo_preferencial: periodoPreferencial || '',
          observacoes: observacoes || '',
        },
      };

      console.log('[handleMeetingRequest] Sending to /api/send-email:', emailPayload);

      const emailResponse = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailPayload),
      });

      const data = await emailResponse.json().catch(() => ({}));

      console.log('[handleMeetingRequest] Email response:', { status: emailResponse.status, data });

      if (!emailResponse.ok || data?.success === false) {
        setMeetingRequestError('Não foi possível enviar o pedido de agendamento. Por favor, tente novamente dentro de instantes.');
      } else {
        setMeetingRequestSent(true);
      }
    } catch (err) {
      console.error('[handleMeetingRequest] Unexpected error:', err);
      setMeetingRequestError('Erro de ligação ao enviar o pedido. Por favor verifique a sua ligação à internet.');
    } finally {
      setMeetingRequestLoading(false);
    }
  };

  const handleCloseContactForm = () => {
    setShowContactForm(false);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div
          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: '#009688', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BrandHeader activePage="results" />
      <ProgressIndicator currentStep={3} completedSteps={[1, 2]} />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8 lg:py-12">
        {/* Save error banner */}
        {saveError && (
          <div
            className="mb-6 flex items-start gap-3 p-4"
            style={{
              borderRadius: '5px',
              backgroundColor: 'rgba(198, 40, 40, 0.05)',
              border: '1px solid rgba(198, 40, 40, 0.25)',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="#C62828"
              className="flex-shrink-0 mt-0.5"
              aria-hidden="true"
            >
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p
              className="text-xs leading-relaxed"
              style={{ fontFamily: 'DM Sans, sans-serif', color: '#C62828' }}
            >
              {saveError}
            </p>
          </div>
        )}

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-8" style={{ backgroundColor: '#009688' }} />
            <span
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '0.72rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#009688',
              }}
            >
              Diagnóstico Concluído
            </span>
          </div>
          <h1
            className="text-gray-900 mb-2"
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '2rem', fontWeight: 700 }}
          >
            Resultados e Recomendações
          </h1>
          <p className="text-gray-500 text-sm" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Análise personalizada para{' '}
            <span className="font-semibold text-gray-900">
              {results.contactInfo.parish || 'a sua Junta de Freguesia'}
            </span>
            {results.contactInfo.municipality ? (
              <> — {results.contactInfo.municipality}</>
            ) : null}
          </p>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <ProfileCard results={results} />
            <ScoreBreakdown areaScores={results.areaScores} totalScore={results.totalScore} />
            <RecommendationsPanel results={results} />
          </div>

          {/* Right Column */}
          <div className="flex flex-col gap-6">
            <ActionPanel
              results={results}
              onContactRequest={handleContactRequest}
              onEmailReport={handleEmailReport}
              emailReportSent={emailReportSent}
              emailReportError={emailReportError}
              emailReportLoading={emailReportLoading}
              onMeetingRequest={handleMeetingRequest}
              meetingRequestSent={meetingRequestSent}
              meetingRequestError={meetingRequestError}
              meetingRequestLoading={meetingRequestLoading}
            />

            {/* Navigation back */}
            <div
              className="bg-white border border-gray-100 p-4"
              style={{ borderRadius: '5px' }}
            >
              <p
                className="text-xs text-gray-400 mb-3"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Pretende rever o diagnóstico?
              </p>
              <Link
                href="/multi-step-diagnostic-form"
                className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
                style={{ fontFamily: 'DM Sans, sans-serif', color: '#009688' }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Refazer diagnóstico
              </Link>
            </div>
          </div>
        </div>
      </main>

      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactRequestForm
          contactInfo={results.contactInfo}
          profile={results.profile}
          onClose={handleCloseContactForm}
        />
      )}

      {/* Footer with real GestPub logo */}
      <footer
        className="mt-auto"
        style={{ borderTop: '1px solid #F0F0F0', backgroundColor: 'white' }}
      >
        <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link
              href="/diagnostic-landing-page"
              aria-label="GestPub — Página inicial"
              className="focus-visible:outline-none"
            >
              <AppImage
                src={GESTPUB_LOGO_URL}
                alt="GestPub — Gestão Pública e Consultoria Autárquica"
                width={100}
                height={28}
                className="h-7 w-auto object-contain"
              />
            </Link>
            <p className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
              © {new Date().getFullYear()} GestPub — Gestão Pública e Consultoria Autárquica.
            </p>
          </div>
          <p className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            Os seus dados são tratados com confidencialidade e em conformidade com o RGPD.
          </p>
        </div>
      </footer>
    </div>
  );
}